import React, { useState } from 'react';

import dayjs from '~/timeInit';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import { useGetCompetitorPropertyHistory } from '~/hooks/apis/price-parity/useGetCompetitorPropertyHistory';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import { DATE_TIME_FORMAT, HISTORY_COLLAPSED_ROWS, HISTORY_PAGE_SIZE } from './constants';
import { collectionToSelect, formatAmountWithAud, shownCollectionId, taxBasisLabel } from './utils';

interface Props {
  property: CompetitorProperty;
  selectedCollectionId?: string;
  /** Undefined goes back to the latest collection. */
  onSelectCollection: (collectionId?: string) => void;
}

export default function CompetitorOfferHistoryTable({ property, selectedCollectionId, onSelectCollection }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const {
    data: history = [],
    isLoading,
    error,
  } = useGetCompetitorPropertyHistory(property.sourceName, property.externalPropertyId);

  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (error) {
    return <Alert severity="error">Failed to load collection history: {error.message}</Alert>;
  }

  const visibleHistory = isExpanded
    ? history.slice(pageIndex * HISTORY_PAGE_SIZE, (pageIndex + 1) * HISTORY_PAGE_SIZE)
    : history.slice(0, HISTORY_COLLAPSED_ROWS);
  const canExpand = history.length > HISTORY_COLLAPSED_ROWS;
  const shownId = shownCollectionId(history, selectedCollectionId);
  const toggleCollection = (collectionId: string) =>
    onSelectCollection(collectionToSelect(collectionId, history, selectedCollectionId));

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Collection started</TableCell>
            <TableCell>Observed</TableCell>
            <TableCell>Lead price</TableCell>
            <TableCell>Was price</TableCell>
            <TableCell>Discount</TableCell>
            <TableCell>Options</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleHistory.map((entry) => (
            <TableRow key={entry.collectionId} selected={entry.collectionId === shownId}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={entry.collectionId === shownId}
                  disabled={!entry.seen}
                  onChange={() => toggleCollection(entry.collectionId)}
                  inputProps={{ 'aria-label': 'Show this collection' }}
                />
              </TableCell>
              <TableCell>
                {entry.collectionStartedAt ? dayjs(entry.collectionStartedAt).format(DATE_TIME_FORMAT) : '—'}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  {entry.seen ? (
                    <Chip size="small" color="success" label="Found" />
                  ) : (
                    <Chip size="small" label="Not found" />
                  )}
                  {!entry.fullRun && <Chip size="small" variant="outlined" label="Partial run" />}
                </Stack>
              </TableCell>
              <TableCell>
                {entry.seen
                  ? `${formatAmountWithAud(entry.leadPrice, entry.leadCurrency, entry.leadPriceAud)} (${taxBasisLabel(entry.leadPriceIncludesTax)})`
                  : '—'}
              </TableCell>
              <TableCell>
                {entry.seen ? formatAmountWithAud(entry.wasPrice, entry.leadCurrency, entry.wasPriceAud) : '—'}
              </TableCell>
              <TableCell>{entry.discountPct !== undefined ? `${entry.discountPct}%` : '—'}</TableCell>
              <TableCell>{entry.optionCount ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isExpanded && history.length > HISTORY_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={history.length}
          page={pageIndex}
          rowsPerPage={HISTORY_PAGE_SIZE}
          rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
          onPageChange={(_, nextPageIndex) => setPageIndex(nextPageIndex)}
        />
      )}
      {canExpand && (
        <Button
          size="small"
          startIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          onClick={() => {
            setIsExpanded(!isExpanded);
            setPageIndex(0);
          }}
          sx={{ mt: 1 }}
        >
          {isExpanded ? 'Show fewer' : 'Show all'}
        </Button>
      )}
    </Box>
  );
}
